import { ClipboardCheck, Wrench } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { exportarCsv } from '@/lib/export';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import { CalibracionNav } from '../components/calibracion-nav';
import { ReporteCalibracionFiltros } from '../components/reporte-filtros';
import { useCalibracionFiltros, useDetalleReporteCalibracion } from '../hooks/use-calibracion';
import { duracionTexto, ejecutorTexto, rangoMesActual } from '../lib/calibracion-format';
import type { DetalleReporteFiltros } from '../calibracion.types';

const COLUMNAS_DETALLES =
  'grid grid-cols-[2.5rem_7rem_minmax(0,1fr)_8rem_minmax(0,1fr)_6.5rem_6.5rem_5rem_5.5rem] items-center gap-x-3';

/**
 * Reporte de detalles de calibración (`/monitoreo/calibracion/reporte/detalles`).
 *
 * Puerto de `Calibration_report::details_report`: una fila por **lado y
 * manguera** calibrada, con el volumen y el importe despachados en esa prueba.
 * Sólo aparecen los puntos que ya tienen transacciones asociadas (así lo hacía
 * el panel anterior con su `INNER JOIN`).
 */
export function CalibracionDetallesPage() {
  useDocumentTitle('Calibración · Detalles');

  const [filtros, setFiltros] = useState<DetalleReporteFiltros>(() => ({
    ...rangoMesActual(),
    executor: 'all',
    producto: 'all',
    bomba: 'all',
  }));

  const catalogo = useCalibracionFiltros();
  const reporte = useDetalleReporteCalibracion(filtros);

  const filas = reporte.data?.details ?? [];
  const resumen = reporte.data?.resumen;

  const exportar = () => {
    exportarCsv(
      'calibracion-detalles',
      [
        'Calibración',
        'Inicio',
        'Ejecutor',
        'Lado',
        'Manguera',
        'Punto de servicio',
        'Producto',
        'Duración (min)',
        'Volumen',
        'Promedio por transacción',
        'Transacciones',
      ],
      filas.map((fila) => [
        fila.calibration_id,
        fila.calibration_start,
        ejecutorTexto(fila.executor_name),
        fila.calibration_pump,
        fila.calibration_nozzle,
        fila.punto_servicio,
        fila.producto_nombre_producto ?? fila.producto_nombre ?? '',
        fila.duracion_calibracion ?? 0,
        fila.volume_data.total_volume.toFixed(3),
        fila.volume_data.promedio_volume.toFixed(3),
        fila.volume_data.total_transactions,
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Monitoreo y control
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <ClipboardCheck className="size-6 text-brand-500" aria-hidden="true" />
            Detalles de calibración
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Detalle por lado y manguera: volumen despachado y transacciones asociadas.
          </p>
        </div>

        <CalibracionNav active="detalles" />
      </header>

      <SectionCard title="Filtros" description="Puedes acotar por producto y lado">
        <ReporteCalibracionFiltros
          filtros={filtros}
          onFiltrosChange={setFiltros}
          catalogo={catalogo.data}
          conProducto
          onExportar={exportar}
          exportandoDeshabilitado={filas.length === 0}
          onConsultar={() => void reporte.refetch()}
          consultando={reporte.isRefetching}
        />
      </SectionCard>

      {reporte.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica el rango de fechas e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void reporte.refetch()}
              loading={reporte.isRefetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {reporte.isPending ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-card" />
          ))}
        </div>
      ) : resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Puntos de servicio
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(resumen.total_detalles)}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              {resumen.total_calibraciones} calibraciones · {resumen.total_bombas} lados
            </p>
          </div>

          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Mangueras y productos
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {resumen.total_mangueras} / {resumen.total_productos}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              {resumen.total_ejecutores} {resumen.total_ejecutores === 1 ? 'ejecutor' : 'ejecutores'}
            </p>
          </div>

          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Volumen calibrado
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatNumber(resumen.volume_stats.total_volume)} gal
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              Promedio {formatNumber(resumen.volume_stats.promedio_volume)} gal por transacción
            </p>
          </div>

          <div className="rounded-card border border-ink-100 bg-white px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Importe
            </p>
            <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink-900">
              {formatCurrency(resumen.volume_stats.total_amount)}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              {formatNumber(resumen.volume_stats.total_transactions)} transacciones ·{' '}
              {formatDateValue(resumen.volume_stats.primera_transaccion)}
            </p>
          </div>
        </div>
      ) : null}

      <SectionCard
        title="Detalle por punto de servicio"
        description="Volumen y transacciones de cada manguera calibrada"
        action={
          <Badge variant="outline">
            {filas.length} {filas.length === 1 ? 'punto' : 'puntos'}
          </Badge>
        }
      >
        {reporte.isPending ? (
          <Skeleton className="h-56 rounded-card" />
        ) : filas.length === 0 ? (
          <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
            <Wrench className="mx-auto size-5 text-ink-300" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-ink-700">Sin detalles en el período</p>
            <p className="mt-0.5 text-xs text-ink-500">
              Sólo se listan los puntos con transacciones de calibración registradas.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[62rem]">
              <div
                className={cn(
                  COLUMNAS_DETALLES,
                  'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                )}
              >
                <span>N°</span>
                <span>Fecha</span>
                <span>Ejecutor</span>
                <span>Punto</span>
                <span>Producto</span>
                <span className="text-right">Volumen</span>
                <span className="text-right">Promedio</span>
                <span className="text-right">Ops</span>
                <span className="text-right">Duración</span>
              </div>

              <ul className="mt-1">
                {filas.map((fila) => (
                  <li
                    key={fila.calibration_detail_id}
                    className={cn(COLUMNAS_DETALLES, 'border-b border-ink-100 py-2 last:border-b-0')}
                  >
                    <span className="font-mono text-xs font-semibold text-ink-700 tabular-nums">
                      #{fila.calibration_id}
                    </span>
                    <span className="text-xs text-ink-600">
                      {formatDateValue(fila.calibration_start)}
                    </span>
                    <span className="truncate text-xs text-ink-700">
                      {ejecutorTexto(fila.executor_name)}
                    </span>
                    <span className="truncate text-xs font-medium text-ink-800">
                      Lado {fila.calibration_pump} · M. {fila.calibration_nozzle}
                    </span>
                    <span className="truncate text-xs text-ink-600">
                      {fila.producto_nombre_producto ?? fila.producto_nombre ?? '—'}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-800 tabular-nums">
                      {formatNumber(fila.volume_data.total_volume)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                      {formatNumber(fila.volume_data.promedio_volume)}
                    </span>
                    <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                      {fila.volume_data.total_transactions}
                    </span>
                    <span className="text-right text-xs text-ink-500">
                      {duracionTexto(fila.duracion_calibracion)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
